import React, { useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import { ReportAPI } from "@ai360/core";

import { Checkbox, DateInput, NoLink, Section, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { messages } from "~/reports/i18n-messages";
import { model } from "~/reports/data";
import { getReportDataByReportType, getReportTypePages } from "~/reports/data/selectors";
import { getSelectedCustomerGuids } from "~/customer-data/selectors";
import { useAppSelector } from "~/store/hooks";

import { ReportOptionsComponentProps } from ".";
import { ReportPageSelection } from "../components/report-page-selection";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { selectors as picklistSelectors, picklistNames } from "~/core/picklist";

import { keywords } from "../keywords";

import "./application-summary.css";

function ApplicationSummary({ onInputChange, initialPreferences }: ReportOptionsComponentProps) {
    const { formatMessage } = useIntl();
    const reportData = useAppSelector(getReportDataByReportType(ApplicationSummary.reportName));
    const selectedCustomerGuids = useAppSelector(getSelectedCustomerGuids);
    const [showPageSelection, setShowPageSelection] = React.useState(false);
    const reportPageSelection = reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};
    const reportPageTypePrefs = initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};
    const reportTypePages = useAppSelector((state) => getReportTypePages(state));

    useEffect(() => {
        onInputChange(model.PROPS_CUSTOMER_GUIDS, Array.from(selectedCustomerGuids));
    }, [selectedCustomerGuids.length]);

    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );

    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const seasonsToDisplay = useRef<string[]>();
    const disableOption = useRef<boolean>(false);

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];

            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === keywords.applicationEvent) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }

            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }

            seasonsText.sort();
            seasonsToDisplay.current = seasonsText;
            disableOption.current =
                selectedEventGuidList.size > 0
                    ? disableReportOptionsOnSelectedEvent(selectedEventGuidList)
                    : false;
        } else {
            seasonsToDisplay.current = [];
            disableOption.current = false;
        }
    }, [selectedEventGuidList.size]);

    const disableReportOptionsOnSelectedEvent = (selectedEventGuidList: any): boolean => {
        for (const eventGeneralGuid of selectedEventGuidList) {
            const event = agEventSummaryMap.get(eventGeneralGuid);
            if (event.agEventTypeName === keywords.applicationEvent) {
                return true;
            }
        }
    };

    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        disableOption.current;

    return (
        <div className="report-options">
            <Section testId="application-summary-options-section">
                <SubSection testId="application-summary-season-subsection">
                    <CroppingSeason
                        disabled={croppingSeasonDisabled}
                        required={!croppingSeasonDisabled}
                        reportType={ApplicationSummary.reportName}
                        valuesToDisplay={seasonsToDisplay.current}
                    />
                </SubSection>
                <SubSection testId="application-summary-dates-subsection">
                    <DateInput
                        placeholderText={formatMessage(messages.startDate)}
                        onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                        timeFormat={false}
                        value={reportData[model.PROPS_START_DATE]}
                        disabled={disableOption.current}
                        testId="application-summary-start-date-date"
                    />
                    <DateInput
                        placeholderText={formatMessage(messages.endDate)}
                        onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                        timeFormat={false}
                        value={reportData[model.PROPS_END_DATE]}
                        disabled={disableOption.current}
                        testId="application-summary-end-date-date"
                    />
                </SubSection>
                <SubSection
                    className="application-summary-form-sub-section"
                    testId="application-summary-product-detail-subsection"
                >
                    <Checkbox
                        className="report-option-checkbox"
                        disabled={false}
                        label={formatMessage(messages.productDetail)}
                        onChange={(e, value) =>
                            onInputChange(
                                model.PROPS_SHOW_APPLICATION_SUMMARY_PRODUCT_DETAIL,
                                value
                            )
                        }
                        value={reportData[model.PROPS_SHOW_APPLICATION_SUMMARY_PRODUCT_DETAIL]}
                        testId="application-summary-product-detail-checkbox"
                    />
                    <Checkbox
                        disabled={false}
                        label={formatMessage(messages.costDetail)}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_SHOW_APPLICATION_SUMMARY_COST_COLUMN, value)
                        }
                        value={reportData[model.PROPS_SHOW_APPLICATION_SUMMARY_COST_COLUMN]}
                        testId="application-summary-cost-detail-checkbox"
                    />
                </SubSection>
                <SubSection testId="application-summary-page-selection-subsection">
                    <NoLink
                        className="categories-link report-options-modal-link page-selection application-summary-page-selection"
                        label={formatMessage(messages.pageSelection)}
                        onClick={() => setShowPageSelection(true)}
                    />
                    <ReportPageSelection
                        isOpen={showPageSelection}
                        onChange={(value) => {
                            onInputChange(
                                model.PROPS_REPORT_TYPE_PAGE_SELECTION,
                                JSON.stringify(value)
                            );
                            setShowPageSelection(false);
                        }}
                        onClose={() => setShowPageSelection(false)}
                        title={formatMessage(messages.pageSelection)}
                        reportTypePages={reportTypePages}
                        reportTypePagesPrefs={reportPageTypePrefs}
                        selectedReportTypes={[ApplicationSummary.reportName]}
                        selectedReportTypePages={reportPageSelection}
                    />
                </SubSection>
            </Section>
        </div>
    );
}

ApplicationSummary.propTypes = {
    reportPreferences: PropTypes.object,
};

ApplicationSummary.defaultReportOptions = {
    [model.PROPS_END_DATE]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID]: null,
    [model.PROPS_REPORT_TYPE_PAGE_SELECTION]: null,
    [model.PROPS_SHOW_APPLICATION_SUMMARY_PRODUCT_DETAIL]: false,
    [model.PROPS_SHOW_APPLICATION_SUMMARY_COST_COLUMN]: false,
};

ApplicationSummary.reportPreferences = [
    model.PROPS_SHOW_APPLICATION_SUMMARY_PRODUCT_DETAIL,
    model.PROPS_SHOW_APPLICATION_SUMMARY_COST_COLUMN,
    model.PROPS_REPORT_TYPE_PAGE_SELECTION,
];

ApplicationSummary.reportName = ReportAPI.ReportNames.APPLICATION_SUMMARY;

export default ApplicationSummary;
